"use client";

import { useAppKit, useAppKitAccount, useAppKitState, useDisconnect, useWalletInfo } from "@reown/appkit/react";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getAddress, isAddress } from "viem";
import { useAccount } from "wagmi";
import { getNodeEnv, publicEnv } from "@/config/env";
import { MAX_CONNECTED_PROFILES } from "@/constants/constants";
import { canStoreAnotherWalletAccount, type ConnectedWalletAccount, getConnectedWalletAccounts, getWalletAddress, removeAuthJwt, setActiveWalletAccount, WALLET_ACCOUNTS_UPDATED_EVENT } from "@/services/auth/auth.utils";
import { useConnectedAccountsUnreadNotifications } from "@/hooks/useConnectedAccountsUnreadNotifications";
import { WalletInitializationError } from "@/src/errors/wallet";
import { SecurityEventType } from "@/src/types/security";
import { createConnectionEventContext, createValidationEventContext, logError, logSecurityEvent } from "@/src/utils/security-logger";
import { isSafeWalletInfo } from "@/utils/wallet-detection";
import { APP_WALLET_CONNECTOR_TYPE } from "@/wagmiConfig/wagmiAppWalletConnector";
import { WalletErrorBoundary } from "./error-boundary";

// Custom error types for better error handling
class WalletConnectionError extends Error {
  constructor(message: string, public readonly cause?: unknown, public readonly code?: string) {
    super(message);
    this.name = "WalletConnectionError";
  }
}
class WalletDisconnectionError extends Error {
  constructor(message: string, public readonly cause?: unknown, public readonly code?: string) {
    super(message);
    this.name = "WalletDisconnectionError";
  }
}
class AuthenticationError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = "AuthenticationError";
  }
}
interface SeizeConnectContextType {
  /** Current connected wallet address, undefined if not connected */
  address: string | undefined;

  /** Name of the connected wallet (e.g. "MetaMask", "Trust Wallet") */
  walletName: string | undefined;

  /** Icon URL of the connected wallet */
  walletIcon: string | undefined;

  /** Whether the connected wallet is a Safe (Gnosis Safe) wallet */
  isSafeWallet: boolean;

  /** Opens the wallet connection modal */
  seizeConnect: () => void;

  /**
   * Disconnects the current wallet connection
   * @throws {WalletDisconnectionError} When disconnection fails
   */
  seizeDisconnect: () => Promise<void>;

  /**
   * Disconnects wallet and clears authentication state
   * @throws {WalletDisconnectionError} When disconnection fails
   * @throws {AuthenticationError} When auth cleanup fails
   */
  seizeDisconnectAndLogout: () => Promise<void>;

  /**
   * Disconnects wallet and clears all authenticated profiles
   * @throws {WalletDisconnectionError} When disconnection fails
   * @throws {AuthenticationError} When auth cleanup fails
   */
  seizeDisconnectAndLogoutAll: () => Promise<void>;

  /**
   * Manually set the connected address (for internal use)
   * @param address - The wallet address to set as connected
   */
  seizeAcceptConnection: (address: string) => void;

  /** Whether the connection modal is currently open */
  seizeConnectOpen: boolean;

  /** Whether a wallet is currently connected to the app */
  isConnected: boolean;

  /** Whether the user is authenticated with a wallet address */
  isAuthenticated: boolean;

  /** Current connection state for better timing control */
  connectionState: "initializing" | "disconnected" | "connecting" | "connected" | "error";

  /** Unified wallet state machine for advanced consumers */
  walletState: WalletState;

  /** Whether there was an initialization error */
  hasInitializationError: boolean;

  /** The initialization error if one occurred */
  initializationError: Error | undefined;

  /** All authenticated wallet accounts available for switching */
  connectedAccounts: readonly {
    readonly address: string;
    readonly role: string | null;
    readonly profileId: string | null;
    readonly profileHandle: string | null;
    readonly isActive: boolean;
    readonly isConnected: boolean;
  }[];

  /** Switches the active authenticated account */
  seizeSwitchConnectedAccount: (address: string) => void;

  /** Opens wallet flow to add another authorized account */
  seizeAddConnectedAccount: () => void;

  /** Whether another account can be added */
  canAddConnectedAccount: boolean;

  /** Unread notification counts keyed by connected account address (lowercased) */
  connectedAccountUnreadNotifications: Readonly<Record<string, number>>;
}
const SeizeConnectContext = createContext<SeizeConnectContextType | undefined>(undefined);

// Error handling utilities
const createWalletError = (ErrorClass: typeof WalletConnectionError | typeof WalletDisconnectionError, operation: string, originalError: unknown): WalletConnectionError | WalletDisconnectionError => {
  const message = originalError instanceof Error ? originalError.message : `Unknown error during ${operation}`;
  return new ErrorClass(`Failed to ${operation}: ${message}`, originalError, originalError instanceof Error ? originalError.name : undefined);
};

// Address validation utilities
interface AddressValidationResult {
  isValid: boolean;
  normalizedAddress?: string | undefined;
  errorContext?: {
    length: number;
    format: "hex_prefixed" | "other";
    debugAddress: string;
  } | undefined;
}
const isCapacitorPlatform = (): boolean => {
  return !!globalThis.window?.Capacitor?.isNativePlatform?.();
};
const normalizeAddress = (address: string): string => address.toLowerCase();
const ADD_FLOW_CANCEL_GRACE_MS: number = 5000;
const validateStoredAddress = (storedAddress: string): AddressValidationResult => {
  // Capacitor-specific validation (more lenient)
  if (isCapacitorPlatform()) {
    if (storedAddress.startsWith("0x") && storedAddress.length === 42) {
      return {
        isValid: true,
        normalizedAddress: storedAddress.toLowerCase()
      };
    }
  }

  // Standard validation using viem
  if (isAddress(storedAddress)) {
    return {
      isValid: true,
      normalizedAddress: getAddress(storedAddress) // checksummed format
    };
  }

  // Invalid address - prepare error context
  const addressLength = storedAddress.length;
  const addressFormat = storedAddress.startsWith("0x") ? "hex_prefixed" : "other";
  const debugAddress = storedAddress.length >= 10 ? storedAddress.slice(0, 10) + "..." : storedAddress;
  return {
    isValid: false,
    errorContext: {
      length: addressLength,
      format: addressFormat,
      debugAddress
    }
  };
};

// Unified Wallet State Machine - eliminates multiple state variables and inconsistencies
type WalletState = {
  status: "initializing";
} | {
  status: "error";
  error: Error;
} | {
  status: "disconnected";
} | {
  status: "connecting";
} | {
  status: "connected";
  address: string;
};

// Initialization error handling utility
const handleInitializationError = (error: unknown, errorContext?: AddressValidationResult["errorContext"]): WalletInitializationError => {
  if (errorContext) {
    // Invalid address error
    logSecurityEvent(SecurityEventType.INVALID_ADDRESS_DETECTED, createValidationEventContext("wallet_initialization", false, errorContext.length, errorContext.format));

    // Clear invalid stored address
    try {
      removeAuthJwt();
    } catch (cleanupError) {
      logError("auth_cleanup_during_init", new Error(`Failed to clear invalid auth state: ${cleanupError}`));
    }
    const initError = new WalletInitializationError("Invalid wallet address found in storage during initialization. This indicates potential data corruption or security breach.", undefined, errorContext.debugAddress);
    logError("wallet_initialization", initError);
    return initError;
  }

  // Unexpected error
  const initError = new WalletInitializationError("Unexpected error during wallet initialization", error);
  logError("wallet_initialization", initError);
  return initError;
};

// Consolidated wallet state management hook with unified state machine
const useConsolidatedWalletState = () => {
  const [walletState, setWalletState] = useState<WalletState>({
    status: "initializing"
  });
  useEffect(() => {
    const initializeWallet = async () => {
      try {
        // Step 1: Retrieve stored address
        const storedAddress: string | null = getWalletAddress();

        // Step 2: Handle no stored address (legitimate case)
        if (!storedAddress) {
          setWalletState({
            status: "disconnected"
          });
          return;
        }

        // Step 3: Validate stored address
        const validationResult = validateStoredAddress(storedAddress);
        if (validationResult.isValid && validationResult.normalizedAddress) {
          // Step 4: Success - set connected state with valid address
          setWalletState({
            status: "connected",
            address: validationResult.normalizedAddress
          });
        } else {
          // Step 4: Error - handle invalid address
          const error_0 = handleInitializationError(undefined, validationResult.errorContext);
          setWalletState({
            status: "error",
            error: error_0
          });
        }
      } catch (error) {
        // Step 4: Error - handle unexpected errors
        const initError = handleInitializationError(error);
        setWalletState({
          status: "error",
          error: initError
        });
      }
    };
    initializeWallet();
  }, []);

  // State transition methods
  const setConnecting = useCallback(() => {
    setWalletState({
      status: "connecting"
    });
  }, []);
  const setConnected = useCallback((address: string) => {
    setWalletState({
      status: "connected",
      address
    });
  }, []);
  const setDisconnected = useCallback(() => {
    setWalletState({
      status: "disconnected"
    });
  }, []);
  const setError = useCallback((error_1: Error) => {
    setWalletState({
      status: "error",
      error: error_1
    });
  }, []);

  // Computed properties for backward compatibility
  const connectedAddress = walletState.status === "connected" ? walletState.address : undefined;
  const hasInitializationError = walletState.status === "error";
  const initializationError = walletState.status === "error" ? walletState.error : undefined;
  const isInitialized = walletState.status !== "initializing";
  return {
    walletState,
    connectedAddress,
    setConnecting,
    setConnected,
    setDisconnected,
    setError,
    // Backward compatibility properties
    hasInitializationError,
    initializationError,
    isInitialized
  };
};
export const SeizeConnectProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const account = useAppKitAccount();
  const wagmiAccount = useAccount();
  const {
    walletInfo
  } = useWalletInfo();
  const {
    disconnect
  } = useDisconnect();
  const {
    open
  } = useAppKit();
  const state = useAppKitState();
  const [storedConnectedAccounts, setStoredConnectedAccounts] = useState<ConnectedWalletAccount[]>(() => getConnectedWalletAccounts());
  const [isAddingConnectedAccount, setIsAddingConnectedAccount] = useState(false);

  // Use consolidated wallet state management
  const {
    walletState,
    connectedAddress,
    setConnecting,
    setConnected,
    setDisconnected,
    hasInitializationError,
    initializationError,
    isInitialized
  } = useConsolidatedWalletState();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const addFlowOriginAddressRef = useRef<string | null>(null);
  const retryConnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAddingConnectedAccountRef = useRef(false);
  const isMountedRef = useRef(true);
  const nodeEnv = getNodeEnv();
  const isDevLikeEnv = nodeEnv === "development" || nodeEnv === "test" || nodeEnv === "local";
  const isLocalHost = globalThis.window !== undefined && (globalThis.window.location.hostname === "localhost" || globalThis.window.location.hostname === "127.0.0.1" || globalThis.window.location.hostname === "::1" || globalThis.window.location.hostname.endsWith(".local"));
  const impersonatedAddress = isDevLikeEnv && isLocalHost && publicEnv.USE_DEV_AUTH === "true" && publicEnv.DEV_MODE_WALLET_ADDRESS && isAddress(publicEnv.DEV_MODE_WALLET_ADDRESS) ? getAddress(publicEnv.DEV_MODE_WALLET_ADDRESS) : undefined;
  const refreshStoredConnectedAccounts = useCallback(() => {
    setStoredConnectedAccounts(getConnectedWalletAccounts());
  }, []);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryConnectTimeoutRef.current) {
        clearTimeout(retryConnectTimeoutRef.current);
        retryConnectTimeoutRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    refreshStoredConnectedAccounts();
    if (globalThis.window === undefined) return;
    const handleAccountsUpdated = () => {
      refreshStoredConnectedAccounts();
    };
    globalThis.addEventListener(WALLET_ACCOUNTS_UPDATED_EVENT, handleAccountsUpdated);
    return () => {
      globalThis.removeEventListener(WALLET_ACCOUNTS_UPDATED_EVENT, handleAccountsUpdated);
    };
  }, [refreshStoredConnectedAccounts]);
  useEffect(() => {
    // Wait for initialization to complete before processing account changes
    if (!isInitialized) {
      return;
    }

    // Clear any existing timeout to debounce rapid changes
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Use debounced state update to prevent race conditions
    debounceTimeoutRef.current = setTimeout(() => {
      if (impersonatedAddress) {
        const isAlreadyConnected = walletState.status === "connected" && walletState.address === impersonatedAddress;
        if (!isAlreadyConnected) {
          setConnected(impersonatedAddress);
        }
        return;
      }
      if (isAddingConnectedAccount && account.address && account.isConnected && isAddress(account.address)) {
        const checksummedConnectedAddress = getAddress(account.address);
        const isAlreadyConnected_0 = walletState.status === "connected" && walletState.address === checksummedConnectedAddress;
        if (!isAlreadyConnected_0) {
          setConnected(checksummedConnectedAddress);
        }
        return;
      }
      const activeStoredAddress = getWalletAddress();
      if (account.address && account.isConnected && isAddress(account.address)) {
        const checksummedConnectedAddress_0 = getAddress(account.address);
        const isKnownStoredAccount = storedConnectedAccounts.some(storedAccount => normalizeAddress(storedAccount.address) === normalizeAddress(checksummedConnectedAddress_0));
        if (isKnownStoredAccount && activeStoredAddress) {
          const isActiveStoredAddressValid = isAddress(activeStoredAddress);
          if (isActiveStoredAddressValid) {
            const checksummedStoredActiveAddress = getAddress(activeStoredAddress);
            const isStoredActiveKnownAccount = storedConnectedAccounts.some(storedAccount_0 => normalizeAddress(storedAccount_0.address) === normalizeAddress(checksummedStoredActiveAddress));
            const isSwitchTransition = normalizeAddress(checksummedConnectedAddress_0) !== normalizeAddress(checksummedStoredActiveAddress);
            if (isStoredActiveKnownAccount && isSwitchTransition) {
              const isAlreadyConnected_1 = walletState.status === "connected" && walletState.address === checksummedStoredActiveAddress;
              if (!isAlreadyConnected_1) {
                setConnected(checksummedStoredActiveAddress);
              }
              return;
            }
          }
        }

        // If wallet is connected to an address that is not in stored profiles yet,
        // prefer the live wallet address so auth can prompt and add it.
        if (!isKnownStoredAccount) {
          const isAlreadyConnected_2 = walletState.status === "connected" && walletState.address === checksummedConnectedAddress_0;
          if (!isAlreadyConnected_2) {
            setConnected(checksummedConnectedAddress_0);
          }
          return;
        }
      }
      if (isAddingConnectedAccount && account.address && isAddress(account.address)) {
        const checksummedCandidateAddress = getAddress(account.address);
        const addFlowOriginAddress = addFlowOriginAddressRef.current;
        const isCandidateDifferentFromOrigin = !addFlowOriginAddress || normalizeAddress(checksummedCandidateAddress) !== normalizeAddress(addFlowOriginAddress);

        // During add-account flow, avoid snapping back to the stored active wallet
        // when we've detected a different candidate address but connection isn't
        // fully established yet.
        if (isCandidateDifferentFromOrigin && !account.isConnected) {
          if (walletState.status !== "connecting") {
            setConnecting();
          }
          return;
        }
      }
      if (activeStoredAddress && isAddress(activeStoredAddress)) {
        const checksummedStoredAddress = getAddress(activeStoredAddress);
        const isAlreadyConnected_3 = walletState.status === "connected" && walletState.address === checksummedStoredAddress;
        if (!isAlreadyConnected_3) {
          setConnected(checksummedStoredAddress);
        }
        return;
      }
      if (account.address && account.isConnected) {
        if (!isAddress(account.address)) {
          // Invalid address from wallet - log security event and disconnect
          const addressStr = account.address as string | undefined;
          logSecurityEvent(SecurityEventType.INVALID_ADDRESS_DETECTED, createValidationEventContext("wallet_provider", false, addressStr?.length || 0, addressStr?.startsWith("0x") ? "hex_prefixed" : "other"));
          setDisconnected();
          return;
        }
        const checksummedAddress = getAddress(account.address);
        const isAlreadyConnected_4 = walletState.status === "connected" && walletState.address === checksummedAddress;
        if (!isAlreadyConnected_4) {
          setConnected(checksummedAddress);
        }
      } else if (account.isConnected === false) {
        if (walletState.status !== "disconnected") {
          setDisconnected();
        }
      } else if (account.status === "connecting") {
        if (walletState.status !== "connecting") {
          setConnecting();
        }
      } else if (walletState.status !== "disconnected") {
        setDisconnected();
      }
    }, 50); // Small delay to debounce rapid changes

    // Cleanup on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [account.address, account.isConnected, account.status, isInitialized, storedConnectedAccounts, walletState, setConnected, setDisconnected, setConnecting, impersonatedAddress, isAddingConnectedAccount]);
  useEffect(() => {
    if (!isAddingConnectedAccount) {
      isAddingConnectedAccountRef.current = false;
      addFlowOriginAddressRef.current = null;
      if (retryConnectTimeoutRef.current) {
        clearTimeout(retryConnectTimeoutRef.current);
        retryConnectTimeoutRef.current = null;
      }
      return;
    }
    let cancelAddFlowTimeout: NodeJS.Timeout | null = null;
    const liveConnectedWallet = account.address && account.isConnected && isAddress(account.address) ? getAddress(account.address) : null;
    const walletAddressCandidate = account.address && isAddress(account.address) ? getAddress(account.address) : null;
    const activeStoredAddress_0 = getWalletAddress();
    const addFlowOriginAddress_0 = addFlowOriginAddressRef.current;
    const isConnectedWalletNowStored = !!liveConnectedWallet && !!activeStoredAddress_0 && normalizeAddress(liveConnectedWallet) === normalizeAddress(activeStoredAddress_0);
    const hasSwitchedFromOrigin = !addFlowOriginAddress_0 || !liveConnectedWallet || normalizeAddress(liveConnectedWallet) !== normalizeAddress(addFlowOriginAddress_0);
    if (isConnectedWalletNowStored && hasSwitchedFromOrigin) {
      setIsAddingConnectedAccount(false);
      addFlowOriginAddressRef.current = null;
      return;
    }
    const addFlowReturnedToOrigin = !state.open && !!liveConnectedWallet && !!addFlowOriginAddress_0 && normalizeAddress(liveConnectedWallet) === normalizeAddress(addFlowOriginAddress_0);
    if (addFlowReturnedToOrigin) {
      setIsAddingConnectedAccount(false);
      addFlowOriginAddressRef.current = null;
      return;
    }
    const hasPendingDifferentCandidate = !!walletAddressCandidate && !!addFlowOriginAddress_0 && normalizeAddress(walletAddressCandidate) !== normalizeAddress(addFlowOriginAddress_0);
    const addFlowCancelled = !state.open && account.status !== "connecting" && account.status !== "reconnecting" && !account.isConnected && !hasPendingDifferentCandidate;
    if (addFlowCancelled) {
      // AppKit/connector state can briefly report "closed + not connected"
      // during successful transitions. Use a short grace period before treating
      // this as a real user-cancelled add flow.
      cancelAddFlowTimeout = setTimeout(() => {
        setIsAddingConnectedAccount(false);
        addFlowOriginAddressRef.current = null;
      }, ADD_FLOW_CANCEL_GRACE_MS);
    }
    return () => {
      if (cancelAddFlowTimeout) {
        clearTimeout(cancelAddFlowTimeout);
      }
    };
  }, [account.address, account.isConnected, account.status, isAddingConnectedAccount, state.open]);
  const activeAddress = impersonatedAddress ?? connectedAddress;
  const liveConnectedAddress = impersonatedAddress || (account.address && account.isConnected && isAddress(account.address) ? getAddress(account.address) : undefined);
  const isActiveWalletConnected = !!(activeAddress && liveConnectedAddress && normalizeAddress(activeAddress) === normalizeAddress(liveConnectedAddress));
  const seizeConnect = useCallback((): void => {
    try {
      // Log connection attempt for security monitoring
      logSecurityEvent(SecurityEventType.WALLET_CONNECTION_ATTEMPT, createConnectionEventContext("seizeConnect"));
      open({
        view: "Connect"
      });

      // Log successful modal opening
      logSecurityEvent(SecurityEventType.WALLET_MODAL_OPENED, createConnectionEventContext("seizeConnect"));
    } catch (error) {
      const connectionError = new WalletConnectionError("Failed to open wallet connection modal", error);
      logError("seizeConnect", connectionError);
      throw connectionError;
    }
  }, [open]);
  const seizeDisconnect = useCallback(async (): Promise<void> => {
    const hasLiveProviderConnection = !!(account.address && account.isConnected && isAddress(account.address));
    if (!hasLiveProviderConnection && !isActiveWalletConnected) {
      return;
    }
    try {
      await disconnect();
    } catch (error_0: unknown) {
      const walletError = createWalletError(WalletDisconnectionError, "disconnect wallet", error_0);
      logError("seizeDisconnect", walletError);
      throw walletError;
    }
  }, [account.address, account.isConnected, disconnect, isActiveWalletConnected]);
  const seizeDisconnectAndLogout = useCallback(async (): Promise<void> => {
    // CRITICAL: Wallet disconnect MUST succeed before auth cleanup
    try {
      await disconnect();
    } catch (error_1: unknown) {
      const walletError_0 = createWalletError(WalletDisconnectionError, "disconnect wallet during logout", error_1);
      logError("seizeDisconnectAndLogout", walletError_0);

      // SECURITY: Throw AuthenticationError to prevent auth bypass
      throw new AuthenticationError("Cannot complete logout: wallet disconnection failed. User may still have active wallet connection.", walletError_0);
    }
    try {
      removeAuthJwt();
      refreshStoredConnectedAccounts();
      const nextActiveAddress = getWalletAddress();
      if (nextActiveAddress && isAddress(nextActiveAddress)) {
        setConnected(getAddress(nextActiveAddress));
      } else {
        setDisconnected();
      }
    } catch (error_2: unknown) {
      const authError = new AuthenticationError("Failed to clear authentication state after successful wallet disconnect", error_2);
      logError("seizeDisconnectAndLogout", authError);
      throw authError;
    }
  }, [disconnect, refreshStoredConnectedAccounts, setConnected, setDisconnected]);
  const seizeDisconnectAndLogoutAll = useCallback(async (): Promise<void> => {
    try {
      await disconnect();
    } catch (error_3: unknown) {
      const walletError_1 = createWalletError(WalletDisconnectionError, "disconnect wallet during logout all profiles", error_3);
      logError("seizeDisconnectAndLogoutAll", walletError_1);
      throw new AuthenticationError("Cannot complete sign out: wallet disconnection failed. User may still have active wallet connection.", walletError_1);
    }
    try {
      let remainingProfiles = getConnectedWalletAccounts().length;
      const maxIterations = Math.max(MAX_CONNECTED_PROFILES * 2, remainingProfiles + 2);
      let iterations = 0;
      while (remainingProfiles > 0) {
        iterations += 1;
        if (iterations > maxIterations) {
          const iterationError = new AuthenticationError(`Failed to clear all authenticated profiles: exceeded ${maxIterations} iterations during logout cleanup.`);
          logError("seizeDisconnectAndLogoutAll", iterationError);
          throw iterationError;
        }
        removeAuthJwt();
        const nextRemainingProfiles = getConnectedWalletAccounts().length;
        if (nextRemainingProfiles >= remainingProfiles) {
          throw new Error("Failed to clear all authenticated profiles.");
        }
        remainingProfiles = nextRemainingProfiles;
      }
      refreshStoredConnectedAccounts();
      setDisconnected();
    } catch (error_4: unknown) {
      if (error_4 instanceof AuthenticationError) {
        throw error_4;
      }
      const authError_0 = new AuthenticationError("Failed to clear all authenticated profiles after successful wallet disconnect", error_4);
      logError("seizeDisconnectAndLogoutAll", authError_0);
      throw authError_0;
    }
  }, [disconnect, refreshStoredConnectedAccounts, setDisconnected]);
  const seizeAcceptConnection = useCallback((address: string): void => {
    // Extract diagnostic data before validation check
    const addressLength = address.length;
    const addressFormat = address.startsWith("0x") ? "hex_prefixed" : "other";
    if (!isAddress(address)) {
      // Log security event with NO address data
      logSecurityEvent(SecurityEventType.INVALID_ADDRESS_DETECTED, createValidationEventContext("seizeAcceptConnection", false, addressLength, addressFormat));
      const error_5 = new AuthenticationError("Invalid Ethereum address format. Address must be a valid EIP-55 checksummed format.");
      logError("seizeAcceptConnection", error_5);
      throw error_5;
    }

    // Log successful address validation with NO address data
    logSecurityEvent(SecurityEventType.ADDRESS_VALIDATION_SUCCESS, createValidationEventContext("seizeAcceptConnection", true));

    // Normalize address to checksummed format for consistency
    const checksummedAddress_0 = getAddress(address);
    setConnected(checksummedAddress_0);
    refreshStoredConnectedAccounts();
  }, [refreshStoredConnectedAccounts, setConnected]);
  const seizeSwitchConnectedAccount = useCallback((address_0: string): void => {
    if (!isAddress(address_0)) {
      throw new AuthenticationError("Cannot switch account: invalid Ethereum address format.");
    }
    const checksummedAddress_1 = getAddress(address_0);
    if (activeAddress && normalizeAddress(activeAddress) === normalizeAddress(checksummedAddress_1)) {
      return;
    }
    const didSwitch = setActiveWalletAccount(checksummedAddress_1);
    if (!didSwitch) {
      throw new AuthenticationError("Cannot switch account: requested account is not available.");
    }
    refreshStoredConnectedAccounts();
    setConnected(checksummedAddress_1);
  }, [activeAddress, refreshStoredConnectedAccounts, setConnected]);
  const canAddConnectedAccount = useMemo(() => {
    return storedConnectedAccounts.length < MAX_CONNECTED_PROFILES;
  }, [storedConnectedAccounts]);
  const activeConnectorType = wagmiAccount.connector?.type;
  const isActiveAppWalletConnector = activeConnectorType === APP_WALLET_CONNECTOR_TYPE;
  const seizeAddConnectedAccount = useCallback((): void => {
    const clearAddConnectedAccountGuard = (): void => {
      isAddingConnectedAccountRef.current = false;
      addFlowOriginAddressRef.current = null;
      if (retryConnectTimeoutRef.current) {
        clearTimeout(retryConnectTimeoutRef.current);
        retryConnectTimeoutRef.current = null;
      }
    };
    if (!canAddConnectedAccount || !canStoreAnotherWalletAccount()) {
      return;
    }
    const liveConnectedWallet_0 = account.address && account.isConnected && isAddress(account.address) ? getAddress(account.address) : null;
    const addFlowOriginAddress_1 = addFlowOriginAddressRef.current;
    const addFlowReturnedToOrigin_0 = !state.open && !!liveConnectedWallet_0 && !!addFlowOriginAddress_1 && normalizeAddress(liveConnectedWallet_0) === normalizeAddress(addFlowOriginAddress_1);
    const hasStaleAddConnectedAccountGuard = isAddingConnectedAccountRef.current && (!isAddingConnectedAccount || addFlowReturnedToOrigin_0 || !state.open && !retryConnectTimeoutRef.current && !liveConnectedWallet_0 && account.status !== "connecting" && account.status !== "reconnecting");
    if (hasStaleAddConnectedAccountGuard) {
      clearAddConnectedAccountGuard();
      setIsAddingConnectedAccount(false);
    }
    if (isAddingConnectedAccountRef.current) {
      return;
    }
    if (!liveConnectedWallet_0 || isActiveAppWalletConnector) {
      isAddingConnectedAccountRef.current = true;
      addFlowOriginAddressRef.current = liveConnectedWallet_0;
      setIsAddingConnectedAccount(true);
      try {
        seizeConnect();
      } catch (error_6: unknown) {
        clearAddConnectedAccountGuard();
        setIsAddingConnectedAccount(false);
        const connectionError_0 = createWalletError(WalletConnectionError, "start add-account connection flow", error_6);
        logError("seizeAddConnectedAccount", connectionError_0);
      }
      return;
    }
    isAddingConnectedAccountRef.current = true;
    addFlowOriginAddressRef.current = liveConnectedWallet_0;
    setIsAddingConnectedAccount(true);
    if (retryConnectTimeoutRef.current) {
      clearTimeout(retryConnectTimeoutRef.current);
      retryConnectTimeoutRef.current = null;
    }
    try {
      disconnect().then(() => {
        retryConnectTimeoutRef.current = setTimeout(() => {
          retryConnectTimeoutRef.current = null;
          if (!isMountedRef.current) {
            clearAddConnectedAccountGuard();
            return;
          }
          try {
            seizeConnect();
          } catch (error_8: unknown) {
            clearAddConnectedAccountGuard();
            setIsAddingConnectedAccount(false);
            const connectionError_1 = createWalletError(WalletConnectionError, "start add-account connection flow", error_8);
            logError("seizeAddConnectedAccount", connectionError_1);
          }
        }, 100);
      }).catch((error_9: unknown) => {
        clearAddConnectedAccountGuard();
        setIsAddingConnectedAccount(false);
        const walletError_3 = createWalletError(WalletDisconnectionError, "disconnect wallet before adding account", error_9);
        logError("seizeAddConnectedAccount", walletError_3);
      });
    } catch (error_7: unknown) {
      clearAddConnectedAccountGuard();
      setIsAddingConnectedAccount(false);
      const walletError_2 = createWalletError(WalletDisconnectionError, "disconnect wallet before adding account", error_7);
      logError("seizeAddConnectedAccount", walletError_2);
    }
  }, [account.address, account.isConnected, account.status, canAddConnectedAccount, disconnect, isActiveAppWalletConnector, isAddingConnectedAccount, seizeConnect, state.open]);
  const connectedAccounts = useMemo(() => {
    return storedConnectedAccounts.map(storedAccount_1 => {
      const isActive = !!activeAddress && normalizeAddress(storedAccount_1.address) === normalizeAddress(activeAddress);
      const isConnectedForAccount = !!(liveConnectedAddress && normalizeAddress(storedAccount_1.address) === normalizeAddress(liveConnectedAddress));
      return {
        address: storedAccount_1.address,
        role: storedAccount_1.role,
        profileId: storedAccount_1.profileId,
        profileHandle: storedAccount_1.profileHandle,
        isActive,
        isConnected: isConnectedForAccount
      };
    });
  }, [activeAddress, liveConnectedAddress, storedConnectedAccounts]);
  const connectedAccountUnreadNotifications = useConnectedAccountsUnreadNotifications(storedConnectedAccounts);
  const contextValue = useMemo((): SeizeConnectContextType => ({
    address: activeAddress,
    walletName: isActiveWalletConnected ? walletInfo?.name : undefined,
    walletIcon: isActiveWalletConnected ? walletInfo?.icon : undefined,
    isSafeWallet: isSafeWalletInfo(walletInfo),
    seizeConnect,
    seizeDisconnect,
    seizeDisconnectAndLogout,
    seizeDisconnectAndLogoutAll,
    seizeAcceptConnection,
    seizeSwitchConnectedAccount,
    seizeAddConnectedAccount,
    seizeConnectOpen: state.open,
    isConnected: isActiveWalletConnected,
    isAuthenticated: !!activeAddress,
    connectionState: walletState.status,
    // Unified state machine
    walletState,
    // Expose unified state for advanced consumers
    hasInitializationError,
    initializationError,
    connectedAccounts,
    canAddConnectedAccount,
    connectedAccountUnreadNotifications
  }), [activeAddress, isActiveWalletConnected, connectedAccounts, walletInfo?.name, walletInfo?.icon, seizeConnect, seizeDisconnect, seizeDisconnectAndLogout, seizeDisconnectAndLogoutAll, seizeAcceptConnection, seizeSwitchConnectedAccount, seizeAddConnectedAccount, state.open, account.isConnected, walletState, hasInitializationError, initializationError, canAddConnectedAccount, connectedAccountUnreadNotifications]);
  return <WalletErrorBoundary>
      <SeizeConnectContext.Provider value={contextValue}>
        {children}
      </SeizeConnectContext.Provider>
    </WalletErrorBoundary>;
};
export const useSeizeConnectContext = () => {
  const context = useContext(SeizeConnectContext);
  if (!context) {
    throw new Error("useSeizeConnectContext must be used within a SeizeConnectProvider");
  }
  return context;
};